import React, { useRef, useEffect } from 'react';
import './App.css';

/* ============ Hooks ============ */
function useTilt(strength = 6) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(hover: none)').matches) return;
    let raf = null;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty('--rx', (-y * strength).toFixed(2) + 'deg');
        el.style.setProperty('--ry', (x * strength).toFixed(2) + 'deg');
      });
    };
    const onLeave = () => {
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [strength]);
  return ref;
}

function Reveal({ children, delay = 0, className = '', ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transitionDelay = delay + 'ms';
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.classList.add('in');
        io.unobserve(el);
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={'reveal ' + className} {...rest}>
      {children}
    </div>
  );
}

/* ============ DATA ============ */

const metrics = [
  { value: '0.3664', name: 'Recall@1' },
  { value: '0.6737', name: 'Recall@5' },
  { value: '0.7896', name: 'Recall@10' },
  { value: '0.8754', name: 'Recall@20' },
  { value: '0.9659', name: 'Recall@50' },
  { value: '0.4569', name: 'MRR@10' },
  { value: '0.9264', name: 'AUROC' },
];


const oracle = [
  { k: 'R@1',  actual: 0.3664, techo: 0.4812, pct: 78.2 },
  { k: 'R@5',  actual: 0.6737, techo: 0.8699, pct: 78.2 },
  { k: 'R@10', actual: 0.7896, techo: 0.9563, pct: 81.9 },
  { k: 'R@20', actual: 0.8754, techo: 1.0000, pct: 87.4 },
  { k: 'R@50', actual: 0.9659, techo: 1.0000, pct: 96.9 },
];

const facts = [
  'Zero-shot · sin reentrenar por enfermedad',
  'Segundos sobre PC de bajos recursos',
  '2,250,197 tripletas Hetionet',
  '245 pares test · 5 seeds',
  'Cadenas auditables con evidencia',
  '+86.6% sobre DRKG+GNN 2020',
];

const signals = [
  { id: 'H_gene',  weight: 0.10, kind: 'Hellinger kNN', source: 'Gen',
    desc: 'Similitud de vecinos sobre el perfil génico del fármaco.' },
  { id: 'Sp_dis',  weight: 0.20, kind: 'Propagación',   source: 'Enf → Enf',
    desc: 'Difunde señal entre enfermedades con genes compartidos (IDF + coseno).' },
  { id: 'Sp_drug', weight: 0.40, kind: 'Propagación',   source: 'Droga → Droga',
    desc: 'Reordena candidatos por vecindad génica sobre el propio Y_train.' },
  { id: 'Sp_side', weight: 0.20, kind: 'Propagación',   source: 'Efectos',
    desc: 'Vecindad por efectos secundarios compartidos. Señal ortogonal (Campillos 2008).' },
];

const phases = [
  { idx: 'Fase 01', title: 'Benchmark', desc: 'Split 60/20/20 estratificado por enfermedad. Motor sobre train con val+test enmascarados.', chips: ['5 seeds', 'val+test masked', 'ceiling teórico', 'macro + micro'] },
  { idx: 'Fase 02', title: 'Candidatos', desc: 'Motor sobre corpus completo. Extrae repurposing y de novo preservando las señales individuales.', chips: ['7 señales', 'repurposing', 'de novo', 'min-score'] },
  { idx: 'Fase 03', title: 'Auditoría', desc: 'Post-motor + ChEMBL + PubMed + GtoPdb + TTD + SIDER. Clasificación académica y cuadrantes.', chips: ['ChEMBL', 'PubMed', 'GtoPdb', 'TTD', 'SIDER'] },
];

const hypotheses = [
  {
    idx: 'Caso 01',
    title: 'Chlorambucil \u2192 melanoma',
    meta: 'Bai et al., J Immunother Cancer 2023 (PMID 36759012) demuestra que chlorambucil depleta PD-L1 via ubiquitinacion (GSK3b/bTRCP), inhibe mTORC1 y reduce celulas madre tumorales en melanoma B16. El motor predijo correctamente que chlorambucil afecta la via PD-L1 en melanoma; el mecanismo molecular especifico (ubiquitinacion) difiere del camino topologico propuesto por el grafo.',
    bottleneck: 'CD274',
    category: 'high_confidence_repurposing',
    categoryLabel: 'Partial discovery (path differs)',
    stars: 5,
    strength: 0.811,
    dwpc: 0.106,
    necessity: true,
    source: 'repurposing',
    quadrant: 'A',
    votes: { gtopdb: 1, ttd: 1, sider: 1 },
    chain: [
      { from: 'Chlorambucil', arrow: 'UBIQUITINATES', to: 'PD-L1 (CD274)' },
      { from: 'PD-L1', arrow: 'DEGRADES VIA GSK3B/bTRCP', to: 'melanoma' },
    ],
  },
  {
    idx: 'Caso 02',
    title: 'Nilotinib \u2192 cancer renal',
    meta: 'Inhibidor de PDGFRA/B. Sinergia preclinica con Sunitinib para superar resistencia. Cadenas causales verificadas contra VHL/HIF1A.',
    bottleneck: 'PDGFRA',
    category: 'high_confidence_repurposing',
    categoryLabel: 'Alta confianza',
    stars: 4,
    strength: 0.837,
    dwpc: 0.073,
    necessity: true,
    source: 'repurposing',
    quadrant: 'A',
    votes: { gtopdb: 1, ttd: 0, sider: 1 },
    chain: [
      { from: 'Nilotinib', arrow: 'INHIBITS', to: 'PDGFRA/B' },
      { from: 'PDGFRA', arrow: 'DRIVES', to: 'kidney cancer' },
    ],
  },
  {
    idx: 'Caso 03',
    title: 'Sunitinib \u2192 leucemia mieloide aguda',
    meta: 'Inhibidor de FLT3. Ensayo Fase I/II muestra 59% de respuesta completa en LMA FLT3-mutada (Fiedler et al., 2015).',
    bottleneck: 'FLT3',
    category: 'high_confidence_repurposing',
    categoryLabel: 'Evidencia clinica',
    stars: 4,
    strength: 0.824,
    dwpc: 0.079,
    necessity: true,
    source: 'repurposing',
    quadrant: 'A',
    votes: { gtopdb: 1, ttd: 1, sider: 0 },
    chain: [
      { from: 'Sunitinib', arrow: 'INHIBITS', to: 'FLT3' },
      { from: 'FLT3', arrow: 'DRIVES', to: 'AML' },
    ],
  },
  {
    idx: 'Caso 04',
    title: 'Ruxolitinib \u2192 cancer renal',
    meta: 'Inhibidor de JAK1/2. Evaluado en ensayo PRISM (Fase 1b) con Retifanlimab para RCC resistente a inmunoterapia (NCT07219576).',
    bottleneck: 'JAK2',
    category: 'alternative_mechanism',
    categoryLabel: 'Ensayo activo',
    stars: 3,
    strength: 0.805,
    dwpc: 0.077,
    necessity: true,
    source: 'repurposing',
    quadrant: 'A',
    votes: { gtopdb: 1, ttd: 0, sider: 1 },
    chain: [
      { from: 'Ruxolitinib', arrow: 'INHIBITS', to: 'JAK1/2' },
      { from: 'JAK2', arrow: 'DRIVES', to: 'kidney cancer' },
    ],
  },
  {
    idx: 'Caso 05',
    title: 'Gefitinib \u2192 melanoma',
    meta: 'ADVERTENCIA: ensayo Fase II (Patel et al., 2011, PMID 21738104) FALLO en melanoma no seleccionado (PFS mediana 1.4 meses, 2 respuestas parciales de 48-52 pacientes). NO recomendado como candidato principal. Solo tiene sentido en subgrupos con expresion de EGFR confirmada por biopsia, que no estan identificados en Hetionet.',
    bottleneck: 'EGFR',
    category: 'alternative_mechanism',
    categoryLabel: 'Contradicted by Phase II',
    stars: 3,
    strength: 0.9890,
    dwpc: 0.079,
    necessity: false,
    source: 'repurposing',
    quadrant: 'B',
    votes: { gtopdb: 0, ttd: 0, sider: 1 },
    chain: [
      { from: 'Gefitinib', arrow: 'INHIBITS', to: 'EGFR' },
      { from: 'EGFR', arrow: 'DRIVES', to: 'melanoma' },
    ],
  },
];


const limitations = {
  title: 'Limitacion en el acceso a LINCS L1000',
  body: 'La validacion transcriptomica con LINCS L1000 se realizo sobre un subconjunto de 41 farmacos (3.4% de los 1,204 candidatos) debido a restricciones de acceso a las APIs publicas (CLUE.io, L1000CDS2). El caso estrella Chlorambucil -> melanoma se identifico dentro de este subconjunto limitado.',
  impact: 'Con acceso completo a LINCS (~20,000 compuestos) y DisGeNET, esperariamos mas candidatos validados, pero no podemos cuantificar cuantos sin los datos. Las proyecciones numericas (24x, 20-30x) son extrapolaciones lineales no validadas.',
};

const audit = [
  { label: 'Protocolo', value: 'Split 60 / 20 / 20 por enfermedad', desc: 'val + test enmascarados. 245 pares evaluables, 2,250,197 tripletas, 5 seeds.', chips: ['5 seeds', 'val+test masked', 'ceiling calculado', 'macro + micro'] },
  { label: 'Rendimiento', value: 'Segundos en PC de bajos recursos', desc: 'Sin GPU. Sin clúster. Sin reentrenamiento por enfermedad. Ejecutable en cualquier workstation.', chips: ['CPU only', 'segundos', 'zero-shot', 'sin reentrenar'] },
  { label: 'Trazabilidad', value: 'Hipótesis + ruta + evidencia', desc: 'Cada candidato conserva la relación gene-drug-disease y la fuente pública que respalda la inferencia.', chips: ['GtoPdb', 'TTD', 'SIDER', 'ChEMBL', 'PubMed'] },
  { label: 'Alcance', value: 'Priorización, no prueba clínica', desc: 'La validación terapéutica requiere evidencia experimental y aprobación regulatoria independiente.', chips: ['DREL 0—4', 'no reemplaza validación', 'no decisión clínica'] },
];

const drel = [
  { level: 0, label: 'Predicción in silico' },
  { level: 1, label: 'In vitro' },
  { level: 2, label: 'Animal' },
  { level: 3, label: 'Clínico humano' },
  { level: 4, label: 'Aprobado' },
];

/* ============ Background network SVG ============ */
function BgNetwork() {
  const nodes = [
    [120, 180], [260, 120], [400, 240], [560, 160], [700, 320], [840, 200], [980, 280], [1120, 160], [1280, 240],
    [180, 420], [340, 460], [500, 380], [660, 500], [820, 440], [960, 520], [1100, 420], [1260, 460],
    [140, 640], [300, 700], [460, 620], [620, 720], [780, 660], [940, 740], [1100, 620], [1240, 700],
    [200, 860], [380, 900], [540, 840], [700, 900], [860, 860], [1020, 900], [1180, 840],
    [80, 320], [440, 80], [760, 100], [1080, 80], [1360, 380], [40, 540], [1380, 600], [60, 800], [1320, 820],
  ];
  const edges = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[i][0] - nodes[j][0];
      const dy = nodes[i][1] - nodes[j][1];
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < 230 && (i + j) % 3 !== 0) edges.push([i, j]);
    }
  }
  return (
    <svg className="bg-network" viewBox="0 0 1440 1000" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g stroke="rgba(11, 20, 40, 0.10)" strokeWidth="0.7" fill="none">
        {edges.map(([i, j], k) => (
          <line key={k} x1={nodes[i][0]} y1={nodes[i][1]} x2={nodes[j][0]} y2={nodes[j][1]} />
        ))}
      </g>
      <g fill="rgba(184, 137, 90, 0.28)">
        {nodes.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 5 === 0 ? 3.2 : 2.2} />
        ))}
      </g>
    </svg>
  );
}

/* ============ Components ============ */

function OracleBar({ item }) {
  return (
    <div className="oracle-row">
      <div className="oracle-k mono">{item.k}</div>
      <div className="oracle-track">
        <div className="oracle-fill" style={{ width: item.pct + '%' }} />
      </div>
      <div className="oracle-values">
        <span className="mono oracle-actual">{item.actual.toFixed(4)}</span>
        <span className="oracle-sep">/</span>
        <span className="mono oracle-techo">{item.techo.toFixed(4)}</span>
      </div>
      <div className="oracle-pct mono">{item.pct.toFixed(1)}%</div>
    </div>
  );
}

function Stars({ n }) {
  return (
    <span className="stars" aria-label={n + ' de 4'}>
      {[1,2,3,4].map(i => (
        <span key={i} className={i <= n ? 'on' : 'off'}>★</span>
      ))}
    </span>
  );
}

function VoteChip({ src, value }) {
  const cls = value > 0 ? 'pos' : (value < 0 ? 'neg' : 'zero');
  const sym = value > 0 ? '+' : (value < 0 ? '−' : '·');
  return (
    <span className={'vote ' + cls}>
      <span className="src">{src}</span>
      <span className="sym">{sym}</span>
    </span>
  );
}

function Wordmark() {
  return (
    <div className="wordmark">
      <div className="wordmark-inner">
        EA<span className="dot">-</span>Hm5
      </div>
      <div className="wordmark-sub">Repurposing Engine</div>
    </div>
  );
}

function Device() {
  const tilt = useTilt(7);
  const bars = Array.from({ length: 28 });
  return (
    <div className="device">
      <div className="device-stack" ref={tilt}>
        <div className="device-back" />
        <div className="device-mid" />
        <div className="device-front">
          <div className="device-head">
            <span className="live">pipeline live</span>
            <span>2026.09.22</span>
          </div>

          <div className="device-screen">
            <div className="screen-bars">
              {bars.map((_, i) => (
                <span key={i} className="bar" style={{ animationDelay: (i * 0.09).toFixed(2) + 's' }} />
              ))}
            </div>
            <div className="screen-label">
              <span>signal / ea-hm5 v2</span>
              <span>phase 01-03</span>
            </div>
          </div>

          <div className="readouts">
            <div className="readout">
              <div className="k">Corpus</div>
              <div className="v">Hetionet</div>
            </div>
            <div className="readout">
              <div className="k">Train</div>
              <div className="v">2.25 M</div>
            </div>
            <div className="readout">
              <div className="k">R@10</div>
              <div className="v bronze">0.7896</div>
            </div>
            <div className="readout">
              <div className="k">AUROC</div>
              <div className="v bronze">0.9264</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SignalCard({ s }) {
  const max = 0.4;
  const pct = (s.weight / max) * 100;
  return (
    <article className="signal-card">
      <div className="signal-head">
        <span className="mono sig-id">{s.id}</span>
        <span className="mono sig-w">{s.weight.toFixed(2)}</span>
      </div>
      <div className="signal-bar">
        <span style={{ width: pct + '%' }} />
      </div>
      <div className="signal-meta">
        <span className="sig-kind">{s.kind}</span>
        <span className="sig-sep">·</span>
        <span className="sig-src">{s.source}</span>
      </div>
      <p className="signal-desc">{s.desc}</p>
    </article>
  );
}

function PhaseCard({ p }) {
  const tilt = useTilt(5);
  return (
    <article className="phase-card" ref={tilt}>
      <div className="idx">{p.idx}</div>
      <h3>{p.title}</h3>
      <p>{p.desc}</p>
      <div className="phase-chips">
        {p.chips.map(c => <span className="chip" key={c}>{c}</span>)}
      </div>
    </article>
  );
}

function HypCard({ item }) {
  const tilt = useTilt(5);
  return (
    <article className="hyp hyp-rich" ref={tilt}>
      <header className="hyp-head">
        <span className="idx">{item.idx}</span>
        <Stars n={item.stars} />
      </header>

      <h3>{item.title}</h3>
      <p className="meta">{item.meta}</p>

      <div className="hyp-category">
        <span className={'cat-dot cat-' + item.category} />
        <span className="cat-label">{item.categoryLabel}</span>
      </div>

      <dl className="hyp-stats">
        <div>
          <dt>Bottleneck</dt>
          <dd className="mono">{item.bottleneck}</dd>
        </div>
        <div>
          <dt>Strength</dt>
          <dd className="mono">{item.strength.toFixed(3)}</dd>
        </div>
        <div>
          <dt>DWPC</dt>
          <dd className="mono">{item.dwpc.toFixed(3)}</dd>
        </div>
        <div>
          <dt>Necesidad</dt>
          <dd className={item.necessity ? 'mono ok' : 'mono no'}>
            {item.necessity ? 'verificada' : 'parcial'}
          </dd>
        </div>
      </dl>

      <div className="votes">
        <VoteChip src="GtoPdb" value={item.votes.gtopdb} />
        <VoteChip src="TTD"    value={item.votes.ttd} />
        <VoteChip src="SIDER"  value={item.votes.sider} />
      </div>

      <div className="hyp-chain">
        {item.chain.map((c, i) => (
          <div className="chain-line" key={i}>
            <span className="mol">{c.from}</span>
            <span className="arrow">──{c.arrow}──▶</span>
            <span className="mol">{c.to}</span>
          </div>
        ))}
      </div>

      <footer className="hyp-foot">
        <span className="foot-item">source: <strong>{item.source}</strong></span>
        <span className="foot-sep">·</span>
        <span className="foot-item">quadrant: <strong>{item.quadrant}</strong></span>
      </footer>
    </article>
  );
}

/* ============ App ============ */

export default function App() {
  return (
    <div className="app-shell">
      <div className="bg-layer" aria-hidden="true">
        <BgNetwork />
        <div className="bg-orbs">
          <span className="o1" />
          <span className="o2" />
          <span className="o3" />
        </div>
      </div>

      <header>
        <div className="container nav">
          <div className="brand">
            <span className="brand-mark">EA</span>
            <span>EA-Hm5</span>
          </div>
          <nav className="nav-pill">
            <a href="#metricas">Metricas</a>
            <a href="#oracle">Oracle</a>
            <a href="#senales">Senales</a>
            <a href="#arquitectura">Pipeline</a>
            <a href="#evidencia">Evidencia</a>
            <a href="#auditoria">Auditoria</a>
          </nav>
          <a className="nav-cta" href="https://easyhelpcare-9afda.web.app">
            EasyHelpCare <span aria-hidden="true">↗</span>
          </a>
        </div>
      </header>

      <main>
        {/* HERO */}
        <section className="hero">
          <div className="container">
            <div className="hero-grid">
              <div>
                <Reveal>
                  <span className="kicker">Pipeline verificado 2026-09-22</span>
                </Reveal>
                <Reveal delay={80}>
                  <h1>
                    <span className="zs">zero-shot</span>
                    Motor de priorizacion <span className="serif">terapeutica</span>, en segundos y sobre hardware modesto.
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="lead">
                    EA-Hm5 corre <strong>sin GPU</strong>, <strong>sin reentrenamiento por enfermedad</strong> y en <strong>segundos sobre un PC de bajos recursos</strong>. Combina 7 señales sobre Hetionet y produce cadenas de evidencia auditables. Sin cajas negras.
                  </p>
                </Reveal>
                <Reveal delay={240}>
                  <div className="actions">
                    <a className="button primary" href="#metricas">Ver metricas</a>
                    <a className="button secondary" href="#evidencia">Cadenas de evidencia</a>
                  </div>
                </Reveal>
              </div>

              <div className="hero-right">
                <Reveal delay={320}><Wordmark /></Reveal>
                <Reveal delay={420}><Device /></Reveal>
              </div>
            </div>

            <Reveal delay={520}>
              <div className="info-strip">
                <div className="info-strip-inner">
                  {facts.map(f => <span className="item" key={f}>{f}</span>)}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* METRICS */}
        <section id="metricas" className="section">
          <div className="container">
            <Reveal>
              <div className="section-head">
                <span className="kicker">Resultados verificados</span>
                <h2>Benchmark <span className="serif">real</span> del motor</h2>
                <p>Validacion interna sobre corpus Hetionet. Protocolo val+test enmascarados, sin contaminacion de entrenamiento.</p>
              </div>
            </Reveal>

            <div className="metric-grid">
              {metrics.map((m, i) => (
                <Reveal key={m.name} delay={i * 60}>
                  <div className="metric">
                    <span className="v">{m.value}</span>
                    <div className="n">{m.name}</div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={400}>
              <div className="metric-compare">
                <div className="mc-col">
                  <div className="mc-title">EA-Hm5 v3</div>
                  <div className="mc-line"><span>R@10</span><strong>0.7896</strong></div>
                  <div className="mc-line"><span>AUROC</span><strong>0.9264</strong></div>
                </div>
                <div className="mc-col">
                  <div className="mc-title">DRKG + GNN (2020)</div>
                  <div className="mc-line"><span>R@10</span><strong>0.42</strong></div>
                  <div className="mc-line"><span>AUROC</span><strong>0.81</strong></div>
                </div>
                <div className="mc-col highlight">
                  <div className="mc-title">Ganancia</div>
                  <div className="mc-line"><span>R@10</span><strong>+86.6%</strong></div>
                  <div className="mc-line"><span>AUROC</span><strong>+14.2%</strong></div>
                </div>
              </div>
            </Reveal>
          </div>
        
        {/* ORACLE */}
        <section id="oracle" className="section tight">
          <div className="container">
            <Reveal>
              <div className="section-head">
                <span className="kicker">Techo del grafo</span>
                <h2>Oracle <span className="serif">alcanzable</span> sobre Hetionet</h2>
                <p>
                  El oracle es el maximo matematico que puede alcanzar cualquier motor dado el grafo.
                  Mide cuantos positivos existen realmente en cada top-K. Nuestro <strong>% del oracle</strong>
                  indica que fraccion de ese techo estamos capturando.
                </p>
              </div>
            </Reveal>

            <Reveal delay={80}>
              <div className="oracle-panel">
                <div className="oracle-head">
                  <span className="mono">metrica</span>
                  <span className="mono">progreso hacia el techo</span>
                  <span className="mono">actual / techo</span>
                  <span className="mono">%</span>
                </div>
                {oracle.map((o, i) => (
                  <Reveal key={o.k} delay={120 + i * 60}>
                    <OracleBar item={o} />
                  </Reveal>
                ))}
              </div>
            </Reveal>

            <Reveal delay={500}>
              <div className="oracle-note">
                <strong>Lectura:</strong> en R@10 el techo del grafo es 0.9563, y el motor captura el 74.3%.
                El margen restante (25.7%) esta limitado por la estructura del corpus, no por la formula de fusion.
              </div>
            </Reveal>
          </div>
        </section>

        </section>

        {/* SIGNALS */}
        <section id="senales" className="section tight">
          <div className="container">
            <Reveal>
              <div className="section-head">
                <span className="kicker">Fusion multi-senal</span>
                <h2>Dos <span className="serif">senales</span> nuevas, cero ruido.</h2>
                <p>El motor v2 usa 3 senales: Hellinger sobre genes + dos de propagacion bipartita. Sin ruido, sin pesos hand-tuneados.</p>
              </div>
            </Reveal>
            <div className="signals-grid">
              {signals.map((s, i) => (
                <Reveal key={s.id} delay={i * 60}>
                  <SignalCard s={s} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* BAND - PHASES */}
        <section id="arquitectura" className="band">
          <div className="container">
            <Reveal>
              <div className="section-head">
                <span className="kicker">Pipeline</span>
                <h2>Tres <span className="serif">fases</span>, un flujo.</h2>
                <p>Del corpus bruto al candidato auditado. Cada fase deja trazabilidad y es reproducible end-to-end.</p>
              </div>
            </Reveal>
            <div className="band-grid">
              {phases.map((p, i) => (
                <Reveal key={p.idx} delay={i * 100}>
                  <PhaseCard p={p} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* HYPOTHESES */}
        <section id="evidencia" className="section">
          <div className="container">
            <Reveal>
              <div className="section-head">
                <span className="kicker">Hipotesis generadas</span>
                <h2>Casos <span className="serif">reales</span> del motor</h2>
                <p>Casos priorizados sobre grafos reales. Los casos 2 y 3 fueron rescatados del rank 11-20 por las senales de propagacion.</p>
              </div>
            </Reveal>
            <div className="hyp-grid">
              {hypotheses.map((h, i) => (
                <Reveal key={h.title} delay={i * 100}>
                  <HypCard item={h} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* AUDIT */}
        <section id="auditoria" className="section tight">
          <div className="container">
            <Reveal>
              <div className="section-head">
                <span className="kicker">Constancia auditable</span>
                <h2>Run, protocolo y <span className="serif">evidencia</span></h2>
                <p>Cada ejecucion deja trazabilidad completa: protocolo, resultado y limite declarado.</p>
              </div>
            </Reveal>
            <div className="audit-list">
              {audit.map((a, i) => (
                <Reveal key={a.label} delay={i * 70}>
                  <div className="audit-item">
                    <div className="label">{a.label}</div>
                    <div className="value">{a.value}</div>
                    <div className="desc">
                      <p>{a.desc}</p>
                      <div className="audit-chips">
                        {a.chips.map(c => <span className="chip" key={c}>{c}</span>)}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* DREL */}
        <section className="section tight">
          <div className="container">
            <Reveal>
              <div className="section-head">
                <span className="kicker">Escala DREL</span>
                <h2>Nivel de <span className="serif">evidencia</span> requerido</h2>
                <p>La salida del motor vive en DREL 0. Todo avance a niveles superiores requiere evidencia experimental externa.</p>
              </div>
            </Reveal>
            <div className="drel-track">
              {drel.map((d, i) => (
                <Reveal key={d.level} delay={i * 60}>
                  <div className={'drel-step' + (d.level === 0 ? ' active' : '')}>
                    <div className="drel-num mono">DREL {d.level}</div>
                    <div className="drel-label">{d.label}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FOUNDERS */}
        <section className="section tight">
          <div className="container">
            <Reveal>
              <div className="section-head">
                <span className="kicker">Equipo</span>
                <h2>Creadores del <span className="serif">proyecto</span></h2>
              </div>
            </Reveal>
            <div className="founders">
              <Reveal delay={0}>
                <article className="founder">
                  <div className="founder-photo">
                    <img src="/images/enrique-riveron.png" alt="Enrique Riveron" />
                  </div>
                  <div>
                    <h3>Enrique Riveron</h3>
                    <div className="role">Scientific Direction</div>
                    <p>Direccion cientifica y diseno de sistemas biomedicos.</p>
                  </div>
                </article>
              </Reveal>
              <Reveal delay={120}>
                <article className="founder">
                  <div className="founder-photo">
                    <img src="/images/adrian-riveron.png" alt="Adrian Riveron" />
                  </div>
                  <div>
                    <h3>Adrian Riveron</h3>
                    <div className="role">Engine Architecture</div>
                    <p>Logica del motor, flujos de evidencia y arquitectura de ranking.</p>
                  </div>
                </article>
              </Reveal>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container">
          <div className="footer-top">
            <div className="footer-brand">
              EA-Hm5, <span className="serif">research software</span>.
            </div>
            <div className="footer-links">
              <a href="https://easyhelpcare-9afda.web.app">EasyHelpCare</a>
              <a href="#metricas">Metricas</a>
              <a href="#auditoria">Auditoria</a>
              <a className="footer-email" href="mailto:info@easyhelpcare.com">info@easyhelpcare.com</a>
            </div>
          </div>
          <div className="footer-bottom">
            <span>EasyHelpCare LLC &middot; Florida, USA</span>
            <span>Not medical advice</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
